import React, { useState } from 'react'
import { object, number, string, bool, shape, any } from 'prop-types'
import { productPropTypes } from 'Types'
import {
  ProductPrice,
  LazyLoadImage,
  Link,
  ProductModal,
  FavouriteButtonIcon
} from 'Blocks'
import {
  withGAEvent,
  TrackLinkHOC,
  path,
  artilceProductCardGALabel,
  createArticleProductCardGAArgs,
  cutTitleToLimit,
  useProcessAffiliateLink,
  isClient
} from 'Utils'
import config, { gaSettings } from 'Config'
import { PRODUCT, PAGE_CONTEXT as p, SKIP_IMAGEKIT_STORES } from 'Constants'

const ArticleProductCard = props => {
  const [modalOpen, setModalOpen] = useState(false)
  const {
    product,
    articleUuid,
    className,
    layoutIndex,
    style,
    showProductName,
    imageWidth,
    isSponsored,
    isEmbedModal,
    cardContext = ''
  } = props

  function handleOpen() {
    if (!props.product.flags.isStocked) {
      setModalOpen(true)
    }
  }

  function handleClose() {
    setModalOpen(false)
  }

  let presentedProduct = product
  if (product === null) {
    presentedProduct = PRODUCT
  }

  const {
    flags: { isDiscounted, isStocked },
    image,
    prices,
    prices: {
      currency: { code: currencyCode },
      raw: rawPrice
    },
    store: { name, network },
    title,
    uuid,
    slug
  } = presentedProduct

  let { affiliateUrl = '' } = presentedProduct
  affiliateUrl = useProcessAffiliateLink(affiliateUrl, network)

  const { all } = gaSettings.article.ArticleProductCard
  const labelProps = {
    articleUuid,
    productUuid: uuid,
    position: layoutIndex,
    destinationUrl: affiliateUrl,
    articleStore: name,
    store: name || isSponsored,
    productSponsoredBy: isSponsored,
    value: rawPrice,
    currencyCode,
  }

  const label = artilceProductCardGALabel(labelProps)
  const additionalProps = { ...labelProps }

  const storeName = isEmbedModal ? cutTitleToLimit(name, 20) : name

  const amplitude = {
    eventProps: {}
  }
  if (isSponsored) {
    amplitude.eventProps.Sponsored = !!isSponsored
    amplitude.eventProps['Sponsored By'] = isSponsored
    amplitude.eventProps['Article UUID'] = articleUuid
    amplitude.eventProps['Component Position'] = layoutIndex
    amplitude.eventProps['Component Type'] = 'Product Card'
    amplitude.eventType = config.amplitude.types.clicks.articleToRetailer
  }

  const GALink = withGAEvent(Link, {
    amplitude,
    category: `${!uuid ? `Custom ` : ``}Article ${cardContext}`,
    action: all.action,
    label: label.join(' - '),
    additionalProps,
  })

  const Image = () => (
    <div className="img-wrapper">
      <LazyLoadImage
        alt={title}
        width={imageWidth}
        image={image || PRODUCT.image}
        useOriginal={SKIP_IMAGEKIT_STORES.includes(storeName.toLowerCase())}
      />
    </div>
  )

  const Details = () => (
    <React.Fragment>
      <h4 className="brand">{storeName}</h4>
      {showProductName ? <h5>{cutTitleToLimit(title)}</h5> : null}
    </React.Fragment>
  )

  const internalLink = path('product', { uuid, slug }, `?ccUuid=${articleUuid}`)

  /* send to interstitial page */
  const outgoingLink = config.interStitialEnabled
    ? path(p.OUTGOING, {
        storeName: name,
        to: encodeURIComponent(affiliateUrl)
      })
    : affiliateUrl

  const url = config.articles.useExternalProductLinks
    ? outgoingLink
    : internalLink

  const resolveTo = () => {
    // if the product is custom i.e has no uuid and a custom url
    if (!uuid && affiliateUrl) {
      return affiliateUrl
    }
    return url
  }

  const linkProps = {
    to: resolveTo(),
    target: '_blank',
    childrenOnly: !isStocked,
    style: {height: '100%', width: '100%'}
  }

  if (config.articles.useExternalProductLinks) {
    linkProps.rel = 'sponsored'
  }

  const LoadProducts = () => {
    if(config.theme && config.theme === 'v1') {
      return (
        <div className={`${props.className} ${props.dataItemsPerRow <= 2 ? 'full-width' : 'multi-product-manager'}`}>
          <div className={props.dataItemsPerRow <= 2 ? 'product-block-image-section' : 'product-block-holder'}>
            <div className={props.dataItemsPerRow <= 2 ? 'product-block-image-holder' : 'product-image-hodler'}>
            <GALink {...linkProps}>
                <TrackLinkHOC id={uuid}>
                  <LazyLoadImage
                    alt={title}
                    className="global-image"
                    image={image || PRODUCT.image}
                    width={(props.dataItemsPerRow <= 2 ? props.width : 230)}
                    // height={(props.dataItemsPerRow <= 2 ? props.height : 288)}
                    // style={{width: (props.dataItemsPerRow <= 2 ? props.width : 230), height: (props.dataItemsPerRow <= 2 ? props.height : 288)}}
                  />
                </TrackLinkHOC>
              </GALink>
              <ProductModal
                itemsPerRow={props.itemsPerRow}
                on={modalOpen}
                close={handleClose}
                productUuid={uuid}
                ccUuid={articleUuid}
              />

              {isClient && uuid && <FavouriteButtonIcon {...{ product }} />}
            </div>
            {props.dataItemsPerRow <= 2 ? (<>
              {showProductName ? <div className="product-block-image-title">{cutTitleToLimit(title, 20)}</div> : null}
              <div className="product-block-image-brand">{storeName}</div>
              <ProductPrice
                className="product-block-price-row"
                {...{ isDiscounted, prices }}
              />
              </>) : (
              <div className="product-image-content-holder">
                {showProductName ? <div className="product-block-title">{cutTitleToLimit(title, 20)}</div> : null}
                <div className="product-block-brand">{storeName}</div>
                <ProductPrice
                  className="product-block-price"
                  {...{ isDiscounted, prices }}
                />
              </div>
            )}
          </div>
        </div>
      )
    } else {
      return (
        <div style={style} className={`${className} product`}>
          <GALink {...linkProps}>
            <TrackLinkHOC id={uuid}>
              <div onClick={handleOpen}>
                <div className="image-link">
                  <Image />
                </div>
              </div>
            </TrackLinkHOC>
          </GALink>

          <div className="footer">
            <Details />
            <ProductPrice
              className="article-product-card-price"
              {...{ isDiscounted, prices }}
            />
          </div>

          <ProductModal
            itemsPerRow={props.itemsPerRow}
            on={modalOpen}
            close={handleClose}
            productUuid={uuid}
            ccUuid={articleUuid}
          />

          {isClient && uuid && <FavouriteButtonIcon {...{ product }} />}
        </div>
      )
    }
  }

  return (
    <LoadProducts />
  )
}

ArticleProductCard.displayName = 'ArticleProductCard'

ArticleProductCard.defaultProps = {
  itemIndex: 0,
  layoutIndex: 0,
  className: '',
  style: {},
  showProductName: true,
  screenType: 'large'
}

ArticleProductCard.propTypes = {
  articleUuid: any,
  cardContext: string,
  className: string,
  imageWidth: any,
  isEmbedModal: any,
  isSponsored: any,
  itemIndex: number,
  itemsPerRow: any,
  layoutIndex: number,
  product: shape({ ...productPropTypes }),
  screenType: string,
  showProductName: bool,
  style: object
}

export default ArticleProductCard
